import React, { useCallback, useEffect, useState } from 'react';
import { EnvelopeOpenIcon, ArrowPathIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import onboardingService from '../../services/onboardingService';
import { errText } from '../../services/scheduleService';
import { isAdmin } from '../../utils/permissions';
import OnboardingReview from './OnboardingReview';

const STATUS = {
  sent: ['Link sent', 'bg-blue-100 text-blue-700'],
  expired: ['Expired', 'bg-slate-200 text-slate-500'],
  submitted: ['Awaiting review', 'bg-amber-100 text-amber-800'],
  approved: ['Approved', 'bg-green-100 text-green-700'],
  rejected: ['Rejected', 'bg-red-100 text-red-700'],
  cancelled: ['Cancelled', 'bg-slate-200 text-slate-500'],
};
const when = (d) => new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });

/**
 * Patient onboarding — the links out to patients and the submissions waiting
 * for review. Hidden when there is nothing to show.
 */
const OnboardingPanel = ({ refreshKey, onChanged, onOpenPatient }) => {
  const admin = isAdmin();
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [links, setLinks] = useState({}); // onboarding_id → { link, email } after a resend
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await onboardingService.invites(all));
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, [all]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (id, fn) => {
    setBusy(id);
    try {
      const r = await fn();
      if (r?.link) setLinks((l) => ({ ...l, [id]: r }));
      await load();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };

  if (!data && !error) return null;
  const invites = data?.invites || [];
  if (!all && invites.length === 0 && !error) return null;

  return (
    <section className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
        <EnvelopeOpenIcon className="h-5 w-5 text-brand-teal" />
        <h2 className="font-bold text-slate-800">Patient onboarding</h2>
        {data?.counts && (
          <span className="text-sm text-slate-500">
            {data.counts.awaiting_review > 0 && <span className="font-semibold text-amber-700">{data.counts.awaiting_review} awaiting review · </span>}
            {data.counts.open_links} link{data.counts.open_links === 1 ? '' : 's'} out
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setAll((a) => !a)} className="text-xs font-semibold text-brand-mid hover:underline">{all ? 'Show current' : 'Show all'}</button>
          <button onClick={load} title="Refresh" className="rounded p-1 text-slate-400 hover:bg-slate-100"><ArrowPathIcon className="h-4 w-4" /></button>
        </div>
      </div>
      {error && <p className="bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto">
        {invites.map((i) => {
          const [label, tone] = STATUS[i.display_status] || [i.display_status, 'bg-slate-100 text-slate-600'];
          const open = i.status === 'sent';
          const resent = links[i.onboarding_id];
          return (
            <li key={i.onboarding_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{label}</span>
              <span className="font-semibold text-slate-700">{i.first_names} {i.surname}</span>
              <span className="text-slate-500">{i.email}</span>
              <span className="text-xs text-slate-400">
                {i.display_status === 'sent' && `expires ${when(i.expires_at)}${i.opened_at ? ' · opened' : ''}`}
                {i.display_status === 'expired' && `expired ${when(i.expires_at)}`}
                {i.status === 'submitted' && `sent ${when(i.submitted_at)}${i.matched_existing ? ' · already a patient' : ''}`}
                {['approved', 'rejected'].includes(i.status) && `${when(i.reviewed_at)}${i.reviewed_by_name ? ` by ${i.reviewed_by_name}` : ''}`}
              </span>
              {i.last_send_error && open && <span className="w-full text-xs text-red-600">Email not sent: {i.last_send_error}</span>}
              {resent && (
                <span className="flex w-full items-center gap-2 text-xs">
                  <span className={resent.email.sent ? 'text-green-700' : 'text-amber-700'}>{resent.email.sent ? 'New link sent.' : `New link made, email not sent: ${resent.email.error}`}</span>
                  <button onClick={() => navigator.clipboard?.writeText(resent.link)} className="inline-flex items-center gap-1 font-semibold text-brand-mid hover:underline">
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" /> Copy link
                  </button>
                </span>
              )}
              <span className="ml-auto flex gap-1">
                {i.status === 'submitted' && admin && (
                  <button onClick={() => setReviewing(i.onboarding_id)} className="rounded-lg bg-brand-gradient px-3 py-1 text-xs font-semibold text-white">Review</button>
                )}
                {i.patient_id && ['approved', 'rejected'].includes(i.status) && (
                  <button onClick={() => onOpenPatient(i.patient_id)} className="rounded-lg px-2 py-1 text-xs font-semibold text-brand-mid hover:bg-slate-100">Open patient</button>
                )}
                {open && (
                  <>
                    <button disabled={busy === i.onboarding_id} onClick={() => act(i.onboarding_id, () => onboardingService.resend(i.onboarding_id))} className="rounded-lg px-2 py-1 text-xs font-semibold text-brand-navy hover:bg-slate-100 disabled:opacity-40">
                      {busy === i.onboarding_id ? '…' : 'Resend'}
                    </button>
                    <button
                      disabled={busy === i.onboarding_id}
                      onClick={() => window.confirm(`Cancel ${i.first_names}’s link? It will stop working.`) && act(i.onboarding_id, () => onboardingService.cancel(i.onboarding_id))}
                      className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-40"
                    >
                      Cancel
                    </button>
                  </>
                )}
              </span>
            </li>
          );
        })}
        {invites.length === 0 && <li className="px-4 py-4 text-center text-sm text-slate-400">No onboarding links yet.</li>}
      </ul>

      {reviewing && (
        <OnboardingReview
          onboardingId={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={() => { setReviewing(null); load(); onChanged(); }}
          onOpenPatient={(id) => { setReviewing(null); onOpenPatient(id); }}
        />
      )}
    </section>
  );
};

export default OnboardingPanel;
